// Stock (docs/108 B2): stock por bodega, movimientos, kardex y ajustes. Todo sale del libro de
// inventario (append-only): el saldo se DERIVA; un ajuste es un movimiento con motivo, nunca una
// edición del saldo. Cantidades en UNIDAD BASE del producto.
import { useMemo, useState } from 'react';
import { useUbicaciones } from '../api/consultas';
import type { Ubicacion } from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { decimal, fecha, fechaHora, STOCK_POR_REVISAR } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { BuscadorProducto, type ProductoElegido } from '../ventas/BuscadorProducto';
import {
  NOMBRE_MOVIMIENTO,
  POR_PAGINA,
  useKardex,
  useMovimientos,
  useResumenProductos,
  useStockDeBodega,
  type FiltroMovimientos,
  type FilaBodega,
  type LineaKardex,
  type MovimientoListado,
  type ProductoResumen,
} from './datos';
import { coincide } from '../comun/coincide';

/** Bodegas de la empresa (BODEGA primero); la primera es la operativa por defecto. */
export function useBodegas(empresaId: string) {
  const q = useUbicaciones(empresaId);
  const lista = useMemo(
    () =>
      [...(q.data ?? [])].sort(
        (a, b) =>
          Number(b.tipo === 'BODEGA') - Number(a.tipo === 'BODEGA') ||
          a.codigo.localeCompare(b.codigo),
      ),
    [q.data],
  );
  return { ...q, lista };
}

export function SelectorBodega({
  bodegas,
  valor,
  alCambiar,
  conTodas = false,
}: {
  bodegas: Ubicacion[];
  valor: string;
  alCambiar: (id: string) => void;
  conTodas?: boolean;
}) {
  return (
    <label className="campo">
      <span className="etiqueta">Bodega</span>
      <select aria-label="Bodega" value={valor} onChange={(e) => alCambiar(e.target.value)}>
        {conTodas && <option value="">Todas</option>}
        {bodegas.map((b) => (
          <option key={b.id} value={b.id}>
            {b.codigo} · {b.nombre}
            {b.tipo && b.tipo !== 'BODEGA' ? ` (${b.tipo.toLowerCase()})` : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

const nombreProducto = (p: ProductoResumen | undefined, id: string) =>
  p ? `${p.sku} · ${p.nombre}` : id.slice(0, 8);

// ================================================================ stock por bodega

export function StockPorBodega({
  alAbrirProducto,
}: {
  alAbrirProducto: (p: { id: string; sku: string; nombre: string }) => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const [elegida, setElegida] = useState('');
  const ubicacionId = elegida || bodegas.lista[0]?.id || '';
  const [texto, setTexto] = useState('');
  const [soloConStock, setSoloConStock] = useState(true);
  const [kardexDe, setKardexDe] = useState<string | null>(null);
  const q = useStockDeBodega(empresaId, ubicacionId);
  const productos = useResumenProductos(
    empresaId,
    (q.data ?? []).map((f) => f.productoId),
  );
  const filas = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return (q.data ?? [])
      .filter((f) => !soloConStock || Number(f.fisico) !== 0)
      .filter((f) => {
        if (!t) return true;
        const p = productos.mapa.get(f.productoId);
        return !!p && coincide(t, p.sku, p.nombre);
      })
      .sort((a, b) =>
        (productos.mapa.get(a.productoId)?.nombre ?? '').localeCompare(
          productos.mapa.get(b.productoId)?.nombre ?? '',
        ),
      );
  }, [q.data, productos.mapa, texto, soloConStock]);
  const columnas: Columna<FilaBodega>[] = [
    {
      id: 'sku',
      titulo: 'SKU',
      celda: (f) => <span className="mono">{productos.mapa.get(f.productoId)?.sku ?? '…'}</span>,
      ancho: '8rem',
    },
    {
      id: 'producto',
      titulo: 'Producto',
      celda: (f) => productos.mapa.get(f.productoId)?.nombre ?? '…',
    },
    {
      id: 'unidad',
      titulo: 'Unidad',
      celda: (f) => productos.mapa.get(f.productoId)?.unidadBase ?? '',
      ancho: '5rem',
    },
    {
      id: 'fisico',
      titulo: 'Físico',
      celda: (f) => decimal(f.fisico),
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 'reservado',
      titulo: 'Reservado',
      celda: (f) => decimal(f.reservado),
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 'disponible',
      titulo: 'Disponible',
      celda: (f) => (
        <b className={f.sobrecomprometido ? 'tono-malo' : ''}>{decimal(f.disponible)}</b>
      ),
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 'aviso',
      titulo: '',
      celda: (f) =>
        f.stockPorRevisar ? (
          <span className="insignia tono-aviso" title={STOCK_POR_REVISAR}>
            por revisar
          </span>
        ) : f.sobrecomprometido ? (
          <span className="insignia tono-malo">sobrecomprometido</span>
        ) : null,
      ancho: '9rem',
    },
  ];
  const total = filas.reduce((n, f) => n + Number(f.fisico), 0);
  return (
    <div className="pantalla consulta" data-pantalla="stock-bodega">
      <div className="barra-filtros">
        <SelectorBodega bodegas={bodegas.lista} valor={ubicacionId} alCambiar={setElegida} />
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar producto en la bodega"
            placeholder="SKU o nombre"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <label className="campo-check">
          <input
            type="checkbox"
            checked={soloConStock}
            onChange={(e) => setSoloConStock(e.target.checked)}
          />{' '}
          Solo con stock
        </label>
        <span className="espaciador" />
        <span className="apagado">
          {filas.length} productos · {decimal(String(total))} unidades base
        </span>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <div className={kardexDe ? 'dos-paneles' : ''}>
        <Grilla
          etiqueta="Stock de la bodega"
          columnas={columnas}
          filas={filas}
          clave={(f) => f.productoId}
          cargando={q.isLoading}
          vacio="La bodega no tiene productos con stock"
          alSeleccionar={(f) => f && kardexDe && setKardexDe(f.productoId)}
          alAbrir={(f) => setKardexDe(f.productoId)}
        />
        {kardexDe && (
          <section className="panel-lateral" aria-label="Kardex del producto">
            <div className="fila-datos">
              <h3>{nombreProducto(productos.mapa.get(kardexDe), kardexDe)}</h3>
              <span className="espaciador" />
              <button
                type="button"
                className="boton-mini"
                onClick={() => {
                  const p = productos.mapa.get(kardexDe);
                  if (p) alAbrirProducto({ id: p.id, sku: p.sku, nombre: p.nombre });
                }}
              >
                Abrir ficha
              </button>
              <button
                type="button"
                className="boton-mini"
                aria-label="Cerrar kardex"
                onClick={() => setKardexDe(null)}
              >
                ✕
              </button>
            </div>
            <TablaKardex empresaId={empresaId} ubicacionId={ubicacionId} productoId={kardexDe} />
          </section>
        )}
      </div>
    </div>
  );
}

// ================================================================ kardex

function TablaKardex({
  empresaId,
  ubicacionId,
  productoId,
}: {
  empresaId: string;
  ubicacionId: string;
  productoId: string;
}) {
  const q = useKardex(empresaId, ubicacionId, productoId);
  // El kardex se lee del más reciente al más antiguo; el saldo corrido viene del servidor.
  const filas = [...(q.data ?? [])].reverse();
  const columnas: Columna<LineaKardex>[] = [
    { id: 'fecha', titulo: 'Fecha', celda: (l) => fecha(l.fechaNegocio), ancho: '7rem' },
    { id: 'tipo', titulo: 'Movimiento', celda: (l) => NOMBRE_MOVIMIENTO[l.tipo] ?? l.tipo },
    {
      id: 'doc',
      titulo: 'Documento',
      celda: (l) => (l.documentoTipo ? `${l.documentoTipo} ${l.documentoFolio ?? ''}` : '—'),
      ancho: '8rem',
    },
    {
      id: 'entra',
      titulo: 'Entra',
      celda: (l) => (l.efecto === 1 ? decimal(l.cantidad) : ''),
      alinear: 'der',
      ancho: '6rem',
    },
    {
      id: 'sale',
      titulo: 'Sale',
      celda: (l) => (l.efecto === -1 ? decimal(l.cantidad) : ''),
      alinear: 'der',
      ancho: '6rem',
    },
    {
      id: 'saldo',
      titulo: 'Saldo',
      celda: (l) => <b>{decimal(l.saldoAcumulado)}</b>,
      alinear: 'der',
      ancho: '7rem',
    },
  ];
  return (
    <>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Kardex"
        columnas={columnas}
        filas={filas}
        clave={(l) => l.movimientoId}
        cargando={q.isLoading}
        vacio="Sin movimientos en esta bodega"
      />
    </>
  );
}

export function Kardex() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const [elegida, setElegida] = useState('');
  const ubicacionId = elegida || bodegas.lista[0]?.id || '';
  const [producto, setProducto] = useState<ProductoElegido | null>(null);
  const [buscando, setBuscando] = useState(false);
  return (
    <div className="pantalla consulta" data-pantalla="kardex">
      <div className="barra-filtros">
        <SelectorBodega bodegas={bodegas.lista} valor={ubicacionId} alCambiar={setElegida} />
        <div className="campo">
          <span className="etiqueta">Producto</span>
          <button
            type="button"
            className="selector-cliente"
            aria-label="Elegir producto"
            onClick={() => setBuscando(true)}
          >
            {producto ? `${producto.producto.sku} · ${producto.producto.nombre}` : 'Buscar (F3)…'}
          </button>
        </div>
      </div>
      {producto ? (
        <TablaKardex
          empresaId={empresaId}
          ubicacionId={ubicacionId}
          productoId={producto.producto.id}
        />
      ) : (
        <p className="apagado vacio-centrado">
          Elige un producto para ver sus movimientos y el saldo corrido en la bodega.
        </p>
      )}
      {buscando && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={null}
          ubicacionId={ubicacionId}
          ubicaciones={bodegas.lista}
          alElegir={(p) => {
            setProducto(p);
            setBuscando(false);
          }}
          alCerrar={() => setBuscando(false)}
        />
      )}
    </div>
  );
}

// ================================================================ movimientos

export function Movimientos({ tipoInicial }: { tipoInicial?: string | undefined }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const [f, setF] = useState<FiltroMovimientos>({
    pagina: 1,
    ...(tipoInicial ? { tipo: tipoInicial } : {}),
  });
  const q = useMovimientos(empresaId, f);
  const productos = useResumenProductos(
    empresaId,
    (q.data ?? []).map((m) => m.productoId),
  );
  const bodega = new Map(bodegas.lista.map((b) => [b.id, b.codigo]));
  const cambiar = (c: Partial<FiltroMovimientos>) => setF((x) => ({ ...x, ...c, pagina: 1 }));
  const columnas: Columna<MovimientoListado>[] = [
    { id: 'fecha', titulo: 'Fecha', celda: (m) => fecha(m.fechaNegocio), ancho: '7rem' },
    {
      id: 'registro',
      titulo: 'Registrado',
      celda: (m) => <span className="apagado">{fechaHora(m.registradoEn)}</span>,
      ancho: '9rem',
    },
    {
      id: 'bodega',
      titulo: 'Bodega',
      celda: (m) => bodega.get(m.ubicacionId) ?? '—',
      ancho: '5rem',
    },
    {
      id: 'producto',
      titulo: 'Producto',
      celda: (m) => nombreProducto(productos.mapa.get(m.productoId), m.productoId),
    },
    {
      id: 'tipo',
      titulo: 'Movimiento',
      celda: (m) => NOMBRE_MOVIMIENTO[m.tipo] ?? m.tipo,
      ancho: '12rem',
    },
    {
      id: 'cantidad',
      titulo: 'Cantidad',
      celda: (m) => (
        <b className={m.efecto === -1 ? 'tono-malo' : 'tono-bueno'}>
          {m.efecto === -1 ? '−' : '+'}
          {decimal(m.cantidad)}
        </b>
      ),
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 'doc',
      titulo: 'Documento',
      celda: (m) => (m.documentoTipo ? `${m.documentoTipo} ${m.documentoFolio ?? ''}` : '—'),
      ancho: '8rem',
    },
    {
      id: 'motivo',
      titulo: 'Motivo',
      celda: (m) => <span className="apagado">{m.motivo ?? ''}</span>,
    },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="movimientos">
      <div className="barra-filtros">
        <SelectorBodega
          bodegas={bodegas.lista}
          valor={f.ubicacionId ?? ''}
          alCambiar={(v) => cambiar({ ubicacionId: v || undefined })}
          conTodas
        />
        <label className="campo">
          <span className="etiqueta">Movimiento</span>
          <select
            aria-label="Tipo de movimiento"
            value={f.tipo ?? ''}
            onChange={(e) => cambiar({ tipo: e.target.value || undefined })}
          >
            <option value="">Todos</option>
            {Object.entries(NOMBRE_MOVIMIENTO).map(([t, n]) => (
              <option key={t} value={t}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Desde</span>
          <input
            type="date"
            aria-label="Desde"
            value={f.desde ?? ''}
            onChange={(e) => cambiar({ desde: e.target.value || undefined })}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Hasta</span>
          <input
            type="date"
            aria-label="Hasta"
            value={f.hasta ?? ''}
            onChange={(e) => cambiar({ hasta: e.target.value || undefined })}
          />
        </label>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Movimientos de stock"
        columnas={columnas}
        filas={q.data ?? []}
        clave={(m) => m.movimientoId}
        cargando={q.isLoading}
        vacio="Sin movimientos con esos filtros"
        paginacion={{
          pagina: f.pagina,
          hayMas: (q.data?.length ?? 0) === POR_PAGINA,
          alCambiar: (pagina) => setF((x) => ({ ...x, pagina })),
        }}
      />
    </div>
  );
}

// ================================================================ ajustes

/** Los movimientos de ajuste del libro (guías, inventario físico y ajustes sueltos de antes). */
export function UltimosAjustes({ empresaId }: { empresaId: string }) {
  const pos = useMovimientos(empresaId, { pagina: 1, tipo: 'ajuste_positivo' });
  const neg = useMovimientos(empresaId, { pagina: 1, tipo: 'ajuste_negativo' });
  const filas = [...(pos.data ?? []), ...(neg.data ?? [])]
    .sort((a, b) => b.registradoEn.localeCompare(a.registradoEn))
    .slice(0, 50);
  const productos = useResumenProductos(
    empresaId,
    filas.map((m) => m.productoId),
  );
  const columnas: Columna<MovimientoListado>[] = [
    { id: 'cuando', titulo: 'Registrado', celda: (m) => fechaHora(m.registradoEn), ancho: '9rem' },
    {
      id: 'producto',
      titulo: 'Producto',
      celda: (m) => nombreProducto(productos.mapa.get(m.productoId), m.productoId),
    },
    {
      id: 'cantidad',
      titulo: 'Cantidad',
      celda: (m) => `${m.efecto === -1 ? '−' : '+'}${decimal(m.cantidad)}`,
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 'doc',
      titulo: 'Origen',
      celda: (m) =>
        m.documentoTipo === 'INV' ? `Inventario N° ${m.documentoFolio}` : 'Ajuste manual',
      ancho: '10rem',
    },
    { id: 'motivo', titulo: 'Motivo', celda: (m) => m.motivo ?? '' },
  ];
  return (
    <Grilla
      etiqueta="Últimos ajustes"
      columnas={columnas}
      filas={filas}
      clave={(m) => m.movimientoId}
      cargando={pos.isLoading || neg.isLoading}
      vacio="Todavía no hay ajustes"
    />
  );
}
